import { useState, useEffect, useMemo, useCallback } from 'react';
import { formatScore } from '@/lib/nflScoreFormat';
import { NFLPlayerAvatar } from './NFLPlayerAvatar';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ChevronDown, ChevronUp, Swords, Trophy, Skull, Bomb, Target, Loader2, ShoppingCart } from 'lucide-react';
import { cn } from '@/lib/utils';
import { NFLPlayerMeta } from './NFLPlayerMeta';
import { useWeeklyProjections } from '@/hooks/useWeeklyProjections';
import { hasPlayed, scoreCell, projectionLabel, yetToPlaySummary, projectedFinal } from '@/lib/nflMatchupScoring';
import { shortPlayerName } from '@/lib/nflGameLine';
import { computeBreakdown, resolveTerms, type ModelTerm, type BreakdownResult } from '@/lib/nflScoringBreakdown';
import NFLPlayerDetailModal from './NFLPlayerDetailModal';
import PlayerNewsIcon from '@/components/player/PlayerNewsIcon';
import { ActiveNewsProvider } from '@/components/player/ActiveNewsContext';
import { isConventionalMode, getConventionalPreset, computeConventionalPoints } from '@/lib/nflConventionalScoring';
import { useNFLLeague } from '@/contexts/NFLLeagueContext';
import { partitionSlots, slotLabel, isStarterSlot, leagueSlotOrder } from '@/lib/nflRosterSlots';
import { buildPastWeekLineup, benchLabel, rowCents, pastWeekScoreText, type WeekScoreRow } from '@/lib/nflWeekLineup';
import type { SlotEligibilityRow } from '@/lib/nflStartablePositions';
import SabotageDeployDialog, { DeployContext } from './SabotageDeployDialog';
import TeamLogo from './TeamLogo';
import { rarityOf, ArtChip, SABOTAGE_GRADIENT } from './sabotageStoreUI';

interface MatchupTeam {
  id: string;
  name: string;
  wins: number;
  losses: number;
  ties: number;
  logo_url: string | null;
}

interface SlotPlayer {
  player_id: string;
  full_name: string;
  position: string;
  nfl_team_abbr: string | null;
  headshot_url: string | null;
  sabotage_points: number;
  trash_points: number;
  anti_points: number;
  // SAB-505: conventional (standard/half_ppr/ppr) points, computed client-side
  // from the raw stats jsonb. 0 for sabotage leagues (unused there).
  conventional_points: number;
  position_slot: string;
  // SAB-1003 row context
  status: string;
  injury_status: string | null;
  byeWeek: number | null;
  game: { scheduled: string; status: string; opponentId: string | null; isHome: boolean } | null;
  opponentAlias: string | null;
  isBye: boolean;
  // SAB-994: merged in by the component, not the fetcher.
  weeklyProj?: number | null;
  /** Completed weeks only: the stored total in exact hundredths (shown to 2 dp). */
  cents?: number;
}

/** One side of the comparison. `past` is set only for a COMPLETED week (rendered from the record). */
interface TeamLineup {
  starters: SlotPlayer[];
  bench: SlotPlayer[];
  past?: {
    /** No score rows exist for this team-week. Shown as such -- NEVER the current roster. */
    notRecorded: boolean;
    /** Starters the engine counted that hold no slot (stale extras, duplicate slots). */
    alsoCounted: SlotPlayer[];
    /** Some slot shown is a reconstruction, not a record -> the note must render. */
    hasDerived: boolean;
    /** Exact sum of every counted starter, in hundredths. */
    starterCents: number;
  };
}

/**
 * One side's contribution list. A `mismatch` renders NOTHING and shouts in the console:
 * a breakdown that does not sum to the stored score means a real scoring discrepancy, not
 * missing data, and showing a user "unavailable" for it would disguise a bug as an absence.
 * `no-stats` and `missing-context` are ordinary states and say so plainly.
 */
function BreakdownSide({ player, result, align }: {
  player: SlotPlayer | undefined;
  result: BreakdownResult | null;
  align: 'left' | 'right';
}) {
  if (!player) return <div className="flex-1" />;
  const cls = align === 'right' ? 'text-right' : 'text-left';

  if (!result || result.blocked === 'mismatch') {
    return <div className={cn('flex-1 min-w-0', cls)} />;
  }
  if (result.blocked === 'no-stats') {
    return <div className={cn('flex-1 min-w-0', cls)}>
      <p className="text-[10px] text-muted-foreground italic">No stats yet</p>
    </div>;
  }
  if (result.blocked === 'missing-context') {
    return <div className={cn('flex-1 min-w-0', cls)}>
      <p className="text-[10px] text-muted-foreground italic">Breakdown unavailable for earlier weeks</p>
    </div>;
  }

  const b = result.breakdown!;
  const trash = b.contributions.filter(c => c.pts > 0);
  const anti = b.contributions.filter(c => c.pts < 0);
  return (
    <div className={cn('flex-1 min-w-0 space-y-1', cls)}>
      {trash.length > 0 && (
        <div className="flex flex-wrap gap-1" style={{ justifyContent: align === 'right' ? 'flex-end' : 'flex-start' }}>
          {trash.map(c => (
            <Badge key={c.event} variant="outline" className="text-[9px] px-1 h-4 border-damage/30 text-damage">
              {c.raw}× {c.event} = +{c.pts.toFixed(2)}
            </Badge>
          ))}
        </div>
      )}
      {anti.length > 0 && (
        <div className="flex flex-wrap gap-1" style={{ justifyContent: align === 'right' ? 'flex-end' : 'flex-start' }}>
          {anti.map(c => (
            <Badge key={c.event} variant="outline" className="text-[9px] px-1 h-4 border-emerald-500/30 text-emerald-400">
              {c.raw}× {c.event} = {c.pts.toFixed(2)}
            </Badge>
          ))}
        </div>
      )}
      {trash.length === 0 && anti.length === 0 && (
        <p className="text-[10px] text-muted-foreground italic">No scoring plays</p>
      )}
    </div>
  );
}

interface NFLMatchupDetailProps {
  /**
   * SAB-1062: opens the propose-trade flow pre-targeted at a team, optionally with a player
   * pre-added to their side. Threaded from NFLStaging so every surface that can open the
   * player-detail modal can also act on its Trade button -- a surface that cannot supply
   * this simply does not render the button, rather than rendering a dead one.
   */
  onTradeWith?: (teamId: string, playerId?: string) => void;
  matchupId: string;
  homeTeam: MatchupTeam;
  awayTeam: MatchupTeam;
  homeScore: number;
  awayScore: number;
  status: string;
  week: number;
  myTeamId?: string;
  compact?: boolean;
  onTrySabotage?: () => void;
  // SAB-540 C3b: route to a top-level NFL tab (e.g. 'sabotages' for the store).
  onNavigate?: (tab: string) => void;
  // SAB-505: league scoring mode. Absent/'sabotage' → the inverted per-player
  // display (unchanged). 'standard'|'half_ppr'|'ppr' → conventional points.
  scoringType?: string;
}

/**
 * SAB-886: the starter slots and their labels come from the LEAGUE.
 *
 * This file held a 13-entry STARTER_SLOT_ORDER and a matching SLOT_LABELS map. Both were
 * wrong for a customised league, and the split below made it worse than a missing row: a
 * player in FLEX2 was not in the starter set, so the box score filed a STARTER under
 * bench. Starters are now identified by exclusion (not BN*, not IR*) exactly as the server
 * defines them, so a slot nobody enumerated is still read as a starter.
 */


const slotColors: Record<string, string> = {
  QB: 'text-red-400', RB: 'text-emerald-400', WR: 'text-sky-400', TE: 'text-amber-400',
  FLX: 'text-purple-400', K: 'text-pink-400', P: 'text-teal-400', DST: 'text-orange-400',
  HC: 'text-indigo-400', IDP: 'text-rose-400',
};

/**
 * SAB-1009: the score cell — projection while the game is ahead, actual once it starts.
 *
 * The projection is deliberately styled DIFFERENTLY (muted, italic) rather than merely
 * smaller: a dimmed "+12.4" that looks like every other score is exactly how someone reads
 * a projection as a result. The tone class only applies once the number is real.
 */
function ScoreCell({ cell, projection }: {
  cell: { primary: string; isPlaceholder: boolean };
  projection?: string | null;
}) {
  return (
    // SAB-1009 alignment: the score and its projection are ONE COLUMN, centred on each
    // other. They used to live in different containers -- the score outside the text block
    // and the projection at the right edge of the meta line inside it -- so they could
    // never line up: right-aligning a one-character dash against a four-character number
    // aligns their right edges, not their centres. A fixed-width, centre-aligned stack is
    // what makes the column read as a vertical unit.
    //
    // The width is fixed rather than intrinsic so the column holds still down the list:
    // "+12.4" and "–" are very different widths, and an intrinsic column would jitter
    // left and right from row to row as scores land.
    <span className="shrink-0 w-11 sm:w-14 flex flex-col items-center leading-tight">
      <span className={cn(
        'font-bold tabular-nums text-[11px]',
        // SAB-1009: a real score is GREEN; a dash is not a score and stays muted. The point
        // is that scored rows read as scored at a glance against a column of dashes, and
        // the pairing with the projection underneath doubles as a projection-accuracy
        // readout -- actual over projected, per player.
        //
        // This deliberately REPLACES the sign-based colour (damage for positive, emerald
        // for negative). Worth stating: that colour is redundant with the glyph, since the
        // number already carries its own '+' or '-', whereas "has this player scored yet"
        // had no encoding at all. Trading a duplicated signal for a missing one.
        cell.isPlaceholder ? 'text-muted-foreground/50' : 'text-emerald-400',
      )}>
        {cell.primary}
      </span>
      {projection && (
        <span className="tabular-nums text-[9px] text-muted-foreground/70 font-medium">
          {projection}
        </span>
      )}
    </span>
  );
}

/**
 * Per-player delta. Delegates to the shared formatter so the "is there a score?"
 * test has ONE definition -- this helper was already correct (it null-checks) but
 * the header's four copies were not, and two predicates answering one question is
 * how they diverged.
 */
function formatPts(val: number | null): string {
  return formatScore(val, true);
}

async function fetchTeamRoster(
  teamId: string,
  week: number,
  leagueId: string,
  seasonYear: number,
  scoringType: string | undefined,
  slotOrder: string[],
): Promise<{ starters: SlotPlayer[]; bench: SlotPlayer[] }> {
  const conventional = isConventionalMode(scoringType);
  const convWeights = conventional ? getConventionalPreset(scoringType as string) : null;
  // 1. Get roster
  const { data: rosterData } = await supabase
    .from('nfl_rosters')
    .select(`
      id, player_id, position_slot,
      nfl_players ( id, full_name, position, headshot_url, nfl_franchise_id, status, injury_status )
    `)
    .eq('team_id', teamId)
    .eq('is_active', true);

  if (!rosterData || rosterData.length === 0) return { starters: [], bench: [] };

  const playerIds = rosterData.map(r => r.player_id).filter(Boolean);

  // 2. Fetch scores from nfl_player_match_scores — the per-league source of
  // truth for scored SAB PTS (SAB-523). The old read hit nfl_player_game_stats,
  // whose sabotage_points is 0 (it is a global, per-game table with no league_id
  // and no per-league sabotage adjustment), so every player rendered +0.0.
  // Key on league_id + week + season_year (the real season, not a hardcoded
  // 2025) + the roster's player_ids. For conventional leagues also pull the raw
  // stats jsonb to compute conventional points client-side (mirrors the engine).
  const scoresMap: Record<string, { sabotage_points: number; trash_points: number; anti_points: number; conventional_points: number }> = {};
  if (playerIds.length > 0) {
    // nfl_player_match_scores is not in the generated Supabase types yet, so
    // cast the table name (same pattern as nfl_sabotage_events / nfl_leagues).
    const { data: scores } = await supabase
      .from('nfl_player_match_scores' as any)
      .select(`player_id, sabotage_points, trash_points, anti_points${conventional ? ', stats' : ''}`)
      .eq('league_id', leagueId)
      .in('player_id', playerIds)
      .eq('week', week)
      .eq('season_year', seasonYear)
      // SAB-824: pin the phase. nfl_player_match_scores holds 2026 PRE week-2 rows on
      // prod, so week + season_year alone mixes preseason scores into a REG box score
      // (the SAB-746 collision). REG matches the slate fetchNflMatchups now selects --
      // if preseason matchups are ever surfaced, both must move together.
      .eq('season_type', 'REG');

    if (scores) {
      for (const s of scores) {
        if (!scoresMap[s.player_id]) {
          scoresMap[s.player_id] = { sabotage_points: 0, trash_points: 0, anti_points: 0, conventional_points: 0 };
        }
        scoresMap[s.player_id].sabotage_points += Number(s.sabotage_points) || 0;
        scoresMap[s.player_id].trash_points += Number(s.trash_points) || 0;
        scoresMap[s.player_id].anti_points += Number(s.anti_points) || 0;
        if (conventional && convWeights) {
          scoresMap[s.player_id].conventional_points += computeConventionalPoints((s as any).stats, convWeights);
        }
      }
    }
  }

  // 3. Franchise abbreviations + this week's games (shared with the completed-week path).
  const franchiseIds = rosterData
    .map(r => (r.nfl_players as any)?.nfl_franchise_id)
    .filter(Boolean);
  const { franchiseMap, franchiseByeMap, gameMap } = await loadWeekGameContext(franchiseIds.length > 0, seasonYear, week);

  // 4. Build players
  const players: SlotPlayer[] = rosterData.map(r => {
    const p = r.nfl_players as any;
    const sc = scoresMap[r.player_id];
    return {
      ...playerContext(p, franchiseMap, franchiseByeMap, gameMap),
      player_id: r.player_id,
      sabotage_points: sc?.sabotage_points ?? 0,
      trash_points: sc?.trash_points ?? 0,
      anti_points: sc?.anti_points ?? 0,
      conventional_points: Math.round((sc?.conventional_points ?? 0) * 100) / 100,
      position_slot: r.position_slot,
    };
  });

  // SAB-886: by exclusion, not by membership of a hardcoded list. A FLEX2 player used to
  // fall out of the starter set and be rendered as bench.
  const starters = players.filter(p => isStarterSlot(p.position_slot));
  const bench = players.filter(p => !isStarterSlot(p.position_slot));

  // Sort by the league's own declared order; anything unrecognised sorts last rather than
  // to the front, which is what indexOf's -1 would have done.
  const rank = (slot: string) => {
    const i = slotOrder.indexOf(slot);
    return i === -1 ? Number.MAX_SAFE_INTEGER : i;
  };
  starters.sort((a, b) => rank(a.position_slot) - rank(b.position_slot));

  return { starters, bench };
}

type GameCtx = { scheduled: string; status: string; opponentId: string | null; isHome: boolean };

/** The display context every row carries: name, team, bye, the week's game and opponent. */
function playerContext(
  p: any,
  franchiseMap: Record<string, string>,
  franchiseByeMap: Record<string, number | null>,
  gameMap: Record<string, GameCtx>,
) {
  const g = p?.nfl_franchise_id ? gameMap[p.nfl_franchise_id] : null;
  return {
    full_name: p?.full_name || 'Unknown',
    position: p?.position || '??',
    nfl_team_abbr: p?.nfl_franchise_id ? (franchiseMap[p.nfl_franchise_id] || null) : null,
    headshot_url: p?.headshot_url || null,
    status: p?.status || 'ACT',
    injury_status: p?.injury_status || null,
    byeWeek: p?.nfl_franchise_id ? (franchiseByeMap[p.nfl_franchise_id] ?? null) : null,
    game: g ?? null,
    opponentAlias: g?.opponentId ? (franchiseMap[g.opponentId] || null) : null,
    isBye: p?.nfl_franchise_id ? !g : false,
  };
}

/**
 * A COMPLETED week, rendered from what the engine scored for THIS team in THIS week --
 * including players dropped since -- and never from today's roster. That fallback was the bug:
 * League of Trash wk3 showed Hurts/Kamara/Downs (benched that week, in today's QB/RB1/FLEX) and
 * omitted Geno Smith, Keenan Allen, Etienne and KC D/ST. See lib/nflWeekLineup.ts.
 */
async function fetchPastWeekTeam(
  teamId: string,
  week: number,
  leagueId: string,
  seasonYear: number,
  fullSlotOrder: string[],
): Promise<TeamLineup> {
  const [{ data: rows }, { data: elig }] = await Promise.all([
    (supabase.from('nfl_player_match_scores' as any)
      .select(`id, player_id, is_starter, position_slot, position_slot_source, trash_points, anti_points, sabotage_points,
        nfl_players ( id, full_name, position, headshot_url, nfl_franchise_id, status, injury_status )`)
      .eq('league_id', leagueId)
      .eq('fantasy_team_id', teamId)
      .eq('week', week)
      .eq('season_year', seasonYear)
      .eq('season_type', 'REG') as any),
    supabase.from('nfl_slot_eligibility' as never).select('position, slot_base'),
  ]);
  const scoreRows = (rows ?? []) as any[];
  const weekRows: WeekScoreRow[] = scoreRows.map((r) => ({
    id: r.id, player_id: r.player_id, position: r.nfl_players?.position ?? null,
    is_starter: !!r.is_starter, position_slot: r.position_slot ?? null,
    position_slot_source: (r.position_slot_source ?? null) as WeekScoreRow['position_slot_source'],
    trash_points: r.trash_points, anti_points: r.anti_points, sabotage_points: r.sabotage_points,
    full_name: r.nfl_players?.full_name ?? null,
  }));

  const lineup = buildPastWeekLineup(weekRows, fullSlotOrder, (elig ?? []) as unknown as SlotEligibilityRow[]);
  if (lineup.status === 'not-recorded') {
    return { starters: [], bench: [], past: { notRecorded: true, alsoCounted: [], hasDerived: false, starterCents: 0 } };
  }

  const franchiseIds = scoreRows.map((r) => r.nfl_players?.nfl_franchise_id).filter(Boolean);
  const { franchiseMap, franchiseByeMap, gameMap } = await loadWeekGameContext(franchiseIds.length > 0, seasonYear, week);
  const byId = new Map(scoreRows.map((r) => [r.id, r]));
  const toPlayer = (w: WeekScoreRow, slot: string): SlotPlayer => {
    const r = byId.get(w.id);
    return {
      ...playerContext(r?.nfl_players, franchiseMap, franchiseByeMap, gameMap),
      player_id: w.player_id,
      sabotage_points: Number(w.sabotage_points) || 0,
      trash_points: Number(w.trash_points) || 0,
      anti_points: Number(w.anti_points) || 0,
      // Stored rows already hold the league's points (conventional leagues store them in
      // trash_points, SAB-505), so the record is shown as-is rather than recomputed.
      conventional_points: rowCents(w) / 100,
      cents: rowCents(w),
      position_slot: slot,
    };
  };

  return {
    starters: lineup.slots.filter((s) => s.row).map((s) => toPlayer(s.row!, s.slot)),
    bench: lineup.bench.map((w) => toPlayer(w, benchLabel(w))),
    past: {
      notRecorded: false,
      alsoCounted: lineup.alsoCounted.map((w) => toPlayer(w, 'ALSO')),
      hasDerived: lineup.hasDerived,
      starterCents: lineup.starterCents,
    },
  };
}

/** This week's franchise aliases, byes and games -- one read each, shared by both paths. */
async function loadWeekGameContext(needed: boolean, seasonYear: number, week: number) {
  const franchiseMap: Record<string, string> = {};
  // SAB-1003: bye week rides on the fetch that already runs for the alias.
  const franchiseByeMap: Record<string, number | null> = {};
  const gameMap: Record<string, GameCtx> = {};
  if (!needed) return { franchiseMap, franchiseByeMap, gameMap };
  {
    // All 32, not just the rostered subset: the opponent's alias has to resolve too, and a
    // team nobody on this roster plays for would otherwise render "vs" with a blank.
    // 32 rows is cheaper than a second round trip.
    const { data: franchises } = await supabase
      .from('nfl_franchises')
      .select('id, alias, bye_week_2026');
    if (franchises) {
      // Typed at the loop, not the select -- see NFLMyTeamPage: a third column tips the
      // builder into a SelectQueryError union and scores on the typecheck ratchet.
      for (const f of franchises as unknown as { id: string; alias: string | null; bye_week_2026: number | null }[]) {
        franchiseMap[f.id] = f.alias || '';
        franchiseByeMap[f.id] = f.bye_week_2026 ?? null;
      }
    }
  }

  /**
   * SAB-1003: this week's game per franchise, for the "Sun 4:25 PM vs DEN" line.
   *
   * The matchup side queried nfl_games not at all, which is why it has never shown a
   * kickoff, an opponent or a bye. All three of season_year, season_type and week are
   * pinned: SAB-746 (PRE week N collides with REG week N), SAB-811 and SAB-823 were each
   * caused by omitting one of them, and SAB-823's version marked every player in every
   * league locked on prod.
   */
  if (seasonYear && week) {
    const { data: weekGames } = await (supabase
      .from('nfl_games' as any)
      .select('home_franchise_id, away_franchise_id, scheduled, status')
      .eq('season_year', seasonYear)
      .eq('season_type', 'REG')
      .eq('week', week) as any);
    if (weekGames) {
      for (const g of weekGames as any[]) {
        gameMap[g.home_franchise_id] = { scheduled: g.scheduled, status: g.status, opponentId: g.away_franchise_id, isHome: true };
        gameMap[g.away_franchise_id] = { scheduled: g.scheduled, status: g.status, opponentId: g.home_franchise_id, isHome: false };
      }
    }
  }
  return { franchiseMap, franchiseByeMap, gameMap };
}

/**
 * Completed weeks only. Two statements the page must make whenever they apply:
 *  - "Slots reconstructed" on EVERY week carrying a derived slot. A reconstruction that looks
 *    like a record is the failure this whole fix exists for.
 *  - "Lineup not recorded" for a team-week with no score rows. Never the current roster.
 */
function PastWeekNotes({ away, home, awayName, homeName }: {
  away?: TeamLineup['past']; home?: TeamLineup['past']; awayName: string; homeName: string;
}) {
  if (!away && !home) return null;
  const derived = !!away?.hasDerived || !!home?.hasDerived;
  const notRecorded = [away?.notRecorded ? awayName : null, home?.notRecorded ? homeName : null].filter(Boolean) as string[];
  if (!derived && notRecorded.length === 0) return null;
  return (
    <div className="px-4 pb-2 space-y-1">
      {derived && (
        <p data-testid="slots-reconstructed" className="text-[10px] text-amber-400/90 leading-snug">
          Slots reconstructed: this week was scored before lineup slots were recorded. Who started
          is the record; which slot each starter filled is reconstructed.
        </p>
      )}
      {notRecorded.map((name) => (
        <p key={name} data-testid="lineup-not-recorded" className="text-[10px] text-muted-foreground leading-snug">
          Lineup not recorded for this week: {name}.
        </p>
      ))}
    </div>
  );
}

/** Starters the engine counted that hold no slot (SAB-1226 stale extras, duplicate slots). Shown, never hidden. */
function AlsoCountedRows({ away, home, onOpen }: {
  away: SlotPlayer[]; home: SlotPlayer[]; onOpen: (playerId: string) => void;
}) {
  const n = Math.max(away.length, home.length);
  if (n === 0) return null;
  const cell = (p: SlotPlayer | undefined, align: 'left' | 'right') => p ? (
    <button type="button" onClick={() => onOpen(p.player_id)}
      className={cn('flex-1 min-w-0 flex items-center gap-2 py-1.5 px-2 sm:px-4', align === 'right' && 'flex-row-reverse')}>
      <span className="truncate text-[11px] text-foreground">{shortPlayerName(p.full_name)}</span>
      <span className="text-[10px] text-muted-foreground shrink-0">{p.position}</span>
      <span className="ml-auto mr-0 text-[11px] font-bold tabular-nums text-muted-foreground shrink-0">
        {p.cents !== undefined ? pastWeekScoreText(p.cents) : '–'}
      </span>
    </button>
  ) : <div className="flex-1" />;
  return (
    <div data-testid="also-counted" className="border-t border-border">
      <p className="px-4 pt-2 text-[10px] text-muted-foreground uppercase tracking-wider font-medium">
        Also counted
      </p>
      {Array.from({ length: n }).map((_, i) => (
        <div key={i} className="flex items-center text-xs border-t border-border/30">
          {cell(away[i], 'left')}
          <div className="w-7 sm:w-9 shrink-0 text-center text-[10px] text-muted-foreground">+</div>
          {cell(home[i], 'right')}
        </div>
      ))}
    </div>
  );
}

export default function NFLMatchupDetail({
  onTradeWith,
  matchupId, homeTeam, awayTeam, homeScore, awayScore, status, week, myTeamId, compact, onTrySabotage, onNavigate, scoringType,
}: NFLMatchupDetailProps) {
  // SAB-523: nfl_player_match_scores is per-league and keyed on season_year, so
  // the box-score read needs both. season_year comes from the league (never a
  // hardcoded literal, so a 2026 league works).
  const { currentLeague, season } = useNFLLeague();
  // SAB-886: this league's starter slots, in its declared order.
  const starterSlotOrder = useMemo(
    () => partitionSlots(currentLeague?.settings?.roster_slots).starters,
    [currentLeague?.settings],
  );
  const leagueId = currentLeague?.id ?? null;
  const seasonYear = season;
  const isConventional = isConventionalMode(scoringType);
  // Per-player points to display: conventional points for conventional leagues,
  // else the precomputed sabotage points (unchanged).
  // SAB-809: in nfl_player_match_scores, `sabotage_points` is the sabotage CARD
  // BONUS, not the player's total -- the engine writes `sabotage_points: sabBonus`
  // and computes the team score as trash + anti + sabBonus. Rendering the column
  // alone showed only the card bonus, which is 0 for every player who was not
  // sabotaged: measured on prod's 2026 PRE wk2, all 252 rows rendered 0.0 while
  // the true average was 3.79 and the max 47.0.
  //
  // The 2025 rows look fine only because a PREVIOUS engine wrote the player total
  // into the same column -- rendered avg 9.15 against a true 18.31, exactly 2x on
  // every week, which is the tell that the column changed meaning underneath this
  // component. SAB-523 fixed the TABLE this reads; the column was still wrong.
  const playerPts = (p: SlotPlayer | undefined): number =>
    p && p.cents !== undefined ? p.cents / 100
    : p ? (isConventional
          ? p.conventional_points
          : Math.round((p.trash_points + p.anti_points + p.sabotage_points) * 100) / 100)
      : 0;
  /** A completed week's row shows the stored record to 2 dp (exact, never re-rounded); a live
   *  row keeps the dash-until-played behaviour. */
  const cellFor = (p: SlotPlayer | undefined) => (p && p.cents !== undefined
    ? { primary: pastWeekScoreText(p.cents), isPlaceholder: false }
    : scoreCell(
        p ? playerPts(p) : null,
        !!p && hasPlayed(p.game ? {
          scheduled: p.game.scheduled, status: p.game.status,
          opponentAlias: p.opponentAlias, isHome: p.game.isHome,
        } : null),
        !!p?.isBye,
      ));
  const [homePlayers, setHomePlayers] = useState<TeamLineup>({ starters: [], bench: [] });
  const [awayPlayers, setAwayPlayers] = useState<TeamLineup>({ starters: [], bench: [] });
  const [isLoading, setIsLoading] = useState(true);
  const [showBench, setShowBench] = useState(false);
  const [detailPlayerId, setDetailPlayerId] = useState<string | null>(null);
  // SAB-1172: the news badge opens the sheet straight on News.
  const [detailTab, setDetailTab] = useState<'stats' | 'news' | undefined>(undefined);
  // SAB parity with MLB's MatchupBoxScore: a row expands to show WHY a player scored what
  // they scored. Inverted scoring is unintuitive, so a number a user cannot explain is a
  // number they will not trust.
  const [expandedSlot, setExpandedSlot] = useState<string | null>(null);
  const [terms, setTerms] = useState<ModelTerm[] | null>(null);
  const [ctxByPlayer, setCtxByPlayer] = useState<Record<string, { stats: unknown; team_lost: boolean | null; position: string | null; outcome_final: boolean | null }>>({});

  // The league's RESOLVED model: global terms with this league's per-league overrides merged
  // on (applies_to, source, event). Without the merge an override league's breakdown would
  // simply fail reconciliation and show nothing.
  useEffect(() => {
    if (!leagueId) return;
    let cancelled = false;
    Promise.all([
      supabase.from('nfl_scoring_model' as any).select('weights').eq('is_active', true).maybeSingle(),
      supabase.from('nfl_scoring_settings' as any).select('weights').eq('league_id', leagueId).maybeSingle(),
    ]).then(([modelRes, settingsRes]) => {
      if (cancelled) return;
      const mw = (modelRes.data as any)?.weights;
      if (!Array.isArray(mw) || mw.length === 0) return;
      setTerms(resolveTerms(mw as ModelTerm[], (settingsRes.data as any)?.weights));
    });
    return () => { cancelled = true; };
  }, [leagueId]);

  // Scoring CONTEXT per player-game. `stats` drives every term; `team_lost` and `position`
  // are the SAB-825 columns a PLAYER breakdown cannot be computed without. DST and COACH
  // carry their own context inside the stats blob, so they are never gated on these.
  useEffect(() => {
    const ids = [
      ...homePlayers.starters, ...homePlayers.bench,
      ...awayPlayers.starters, ...awayPlayers.bench,
    ].map(p => p.player_id);
    if (ids.length === 0 || seasonYear === null) return;
    let cancelled = false;
    supabase
      .from('nfl_player_game_stats' as any)
      .select('player_id, stats, team_lost, position, outcome_final')
      .in('player_id', ids)
      .eq('week', week)
      .eq('season_year', seasonYear)
      // SAB-824: same REG pin as the match-scores read above; nfl_player_game_stats
      // carries 2026 PRE rows for weeks 0-2.
      .eq('season_type', 'REG')
      .then(({ data }) => {
        if (cancelled || !data) return;
        const m: Record<string, { stats: unknown; team_lost: boolean | null; position: string | null; outcome_final: boolean | null }> = {};
        for (const r of data as any[]) {
          m[r.player_id] = { stats: r.stats, team_lost: r.team_lost ?? null, position: r.position ?? null, outcome_final: r.outcome_final ?? null };
        }
        setCtxByPlayer(m);
      });
    return () => { cancelled = true; };
  }, [homePlayers, awayPlayers, week, seasonYear]);

  useEffect(() => {
    let cancelled = false;
    // Wait until the league (id + season_year) is known before reading the
    // per-league score table, otherwise the query would return nothing.
    if (!leagueId || seasonYear === null) return;
    setIsLoading(true);

    // A COMPLETED week renders from what was scored (nfl_player_match_scores for that team and
    // week). Only the live week reads the current roster, because only it can still change.
    // Never mix: the current roster standing in for a finished week is the bug this replaces.
    const completed = status === 'final' || status === 'completed';
    const fullSlotOrder = leagueSlotOrder(currentLeague?.settings?.roster_slots);
    const load = (teamId: string) => completed
      ? fetchPastWeekTeam(teamId, week, leagueId, seasonYear, fullSlotOrder)
      : fetchTeamRoster(teamId, week, leagueId, seasonYear, scoringType, starterSlotOrder);
    Promise.all([load(homeTeam.id), load(awayTeam.id)]).then(([home, away]) => {
      if (cancelled) return;
      setHomePlayers(home);
      setAwayPlayers(away);
      setIsLoading(false);
    });

    return () => { cancelled = true; };
  }, [homeTeam.id, awayTeam.id, week, scoringType, leagueId, seasonYear, status]);

  const isFinal = status === 'final' || status === 'completed';
  /**
   * SAB-994: projected team totals for the matchup header.
   *
   * Starters only -- a bench player contributes nothing to the score, so summing them would
   * inflate the projection above anything the matchup can produce.
   *
   * Hidden once the game is FINAL: the actual result is the answer, and a projection beside
   * a finished score invites "we expected more", which is noise on a settled matchup.
   */
  const allPlayerIds = useMemo(() => [
    ...homePlayers.starters, ...homePlayers.bench,
    ...awayPlayers.starters, ...awayPlayers.bench,
  ].map(p => p.player_id), [homePlayers, awayPlayers]);
  const weeklyProjMap = useWeeklyProjections(allPlayerIds, week);

  /**
   * SAB-1009 item 4: re-render exactly when the next kickoff passes.
   *
   * The flip from projection-primary to actual-primary is driven by hasPlayed(), which
   * compares the kickoff to `new Date()` at RENDER time. Something therefore has to cause a
   * render at kickoff, and the surrounding 45s poll does not reliably do it: react-query's
   * structural sharing returns the SAME data reference when a refetch changes nothing, so a
   * poll between kickoff and the first stat write produces no re-render at all. The rows
   * would keep showing projections for a game already under way.
   *
   * A timer to the earliest pending kickoff is exact and self-limiting: it arms once, fires
   * once, and re-arms only while some starter still has a game ahead of him. When everyone
   * has played there is nothing to schedule and it stops. That is cheaper than shortening
   * the poll and it flips the row at the right second rather than up to 45 later.
   */
  const [, forceTick] = useState(0);
  useEffect(() => {
    const kickoffs = [...homePlayers.starters, ...awayPlayers.starters]
      .map(p => p.game?.scheduled)
      .filter((v): v is string => !!v)
      .map(v => new Date(v).getTime())
      .filter(t => !Number.isNaN(t) && t > Date.now());
    if (kickoffs.length === 0) return;
    // +1000ms so the comparison inside hasPlayed is unambiguously past the boundary rather
    // than landing exactly on it.
    const delay = Math.min(...kickoffs) - Date.now() + 1000;
    const id = setTimeout(() => forceTick(n => n + 1), Math.max(delay, 1000));
    return () => clearTimeout(id);
  }, [homePlayers.starters, awayPlayers.starters]);

  /**
   * SAB-1009: per side, what is banked, what is still expected, and who is left.
   *
   * projectedFinal is banked + remaining, never a plain sum of projections: once a player
   * has played, his projection is history and his actual is the fact. Adding both would
   * double-count him, and showing projections alone would ignore the result so far.
   */
  const sides = useMemo(() => {
    const actualOf = (p: unknown) => playerPts(p as SlotPlayer);
    const projOf = (p: unknown) => weeklyProjMap.get((p as SlotPlayer).player_id);
    const build = (starters: SlotPlayer[]) => ({
      ...projectedFinal(starters, actualOf, projOf),
      yetToPlay: yetToPlaySummary(starters),
    });
    return { home: build(homePlayers.starters), away: build(awayPlayers.starters) };
    // playerPts is defined inline above and is stable for a given scoring mode.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [homePlayers.starters, awayPlayers.starters, weeklyProjMap, isConventional]);

  const homeWins = homeScore > awayScore;
  const awayWins = awayScore > homeScore;
  const tied = homeScore === awayScore;

  // Determine if user is home or away
  const isMyHome = myTeamId === homeTeam.id;
  const isMyAway = myTeamId === awayTeam.id;
  const isMyMatchup = isMyHome || isMyAway;

  return (
    <ActiveNewsProvider sport="nfl" keys={[...homePlayers.starters, ...awayPlayers.starters, ...homePlayers.bench, ...awayPlayers.bench].map(p => p.player_id).filter(Boolean) as string[]}>
    <Card className={cn(
      'bg-card border-border overflow-hidden',
      isMyMatchup && 'border-primary/30'
    )}>
      {/* Matchup Header */}
      <div className="bg-gradient-card p-4">
        <div className="flex items-center justify-between mb-1">
          <Badge
            className={cn(
              'text-[10px] border',
              status === 'live' || status === 'in_progress'
                ? 'bg-green-500/10 text-green-400 border-green-500/20 animate-pulse'
                : isFinal
                ? 'bg-muted/10 text-muted-foreground border-border/20'
                : 'bg-primary/10 text-primary border-primary/20'
            )}
          >
            {status === 'live' || status === 'in_progress' ? 'LIVE' : isFinal ? 'FINAL' : 'UPCOMING'}
          </Badge>
          <span className="text-[10px] text-muted-foreground">Week {week}</span>
        </div>

        {/*
          SAB-1015 (header): the two team columns need min-w-0 to be able to shrink.

          `truncate` was already on the team name, and it was INERT: a flex child's
          min-width defaults to `auto`, which is the max-content width of its longest child,
          so each column sized itself to the untruncated name and the row ran off the
          screen. Measured at 390 with a long name: the home column spanned 303 -> 558, i.e.
          168px past the viewport, with its logo clipped and both names reporting
          scrollWidth === clientWidth (not truncated at all).

          The page never scrolled -- documentElement.scrollWidth stayed 390 -- so the only
          symptom was content sliced off at the edge, which is why it read as a broken card
          rather than a wide one. Same root cause as the roster rows in #447, one layer up.
        */}
        <div className="flex items-center justify-between gap-3 mt-3">
          {/* Away Team */}
          <div className="flex-1 min-w-0 text-center">
            <TeamLogo
              logoUrl={awayTeam.logo_url}
              name={awayTeam.name}
              className={cn(
                'h-11 w-11 border mx-auto mb-1.5',
                isMyAway ? 'bg-primary/10 border-primary/20' : 'bg-muted/50 border-border',
              )}
              textClassName={cn('text-base', isMyAway ? 'text-primary' : 'text-foreground')}
            />
            <p className={cn(
              'text-sm font-medium truncate min-w-0',
              isMyAway ? 'text-primary' : 'text-foreground'
            )}>
              {awayTeam.name}
            </p>
            <p className="text-[10px] text-muted-foreground">
              {awayTeam.wins}-{awayTeam.losses}{awayTeam.ties ? `-${awayTeam.ties}` : ''}
            </p>
            <p className={cn(
              'text-2xl font-bold mt-1 tabular-nums',
              isFinal && awayWins ? 'text-damage' : isFinal && homeWins ? 'text-muted-foreground' : 'text-foreground'
            )}>
              {formatScore(awayScore)}
            </p>
            {/* SAB-994: projected total, starters only, hidden once final. */}
            {/* SAB-1009: PROJECTED FINAL under the actual -- banked + still expected, not a
                bare sum of projections. Hidden once nothing is left to play, so a finished
                matchup does not print its own score twice. */}
            {sides.away.projectedRemaining !== null && (
              <p className="text-[10px] text-muted-foreground tabular-nums">
                proj {sides.away.projectedFinal.toFixed(1)}
              </p>
            )}
            {/* SAB-1009: what is left, at a glance. */}
            {sides.away.yetToPlay.count > 0 && (
              <p className="text-[9px] text-muted-foreground/70 leading-tight mt-0.5">
                Yet to play ({sides.away.yetToPlay.count})
                <span className="block">{sides.away.yetToPlay.label}</span>
              </p>
            )}
            {isFinal && awayWins && (
              <div className="flex items-center justify-center gap-1 mt-0.5">
                <Trophy className="h-3 w-3 text-amber-400" />
                <span className="text-[10px] text-amber-400 font-bold">W</span>
              </div>
            )}
          </div>

          {/* VS */}
          <div className="flex flex-col items-center gap-1 shrink-0">
            <Swords className="h-5 w-5 text-muted-foreground" />
            {isFinal && !tied && (
              <span className={cn(
                'text-[10px] font-bold',
                (homeWins ? homeScore : awayScore) > 0 ? 'text-damage/60' : 'text-muted-foreground'
              )}>
                +{Math.abs(homeScore - awayScore).toFixed(1)}
              </span>
            )}
          </div>

          {/* Home Team */}
          <div className="flex-1 min-w-0 text-center">
            <TeamLogo
              logoUrl={homeTeam.logo_url}
              name={homeTeam.name}
              className={cn(
                'h-11 w-11 border mx-auto mb-1.5',
                isMyHome ? 'bg-primary/10 border-primary/20' : 'bg-muted/50 border-border',
              )}
              textClassName={cn('text-base', isMyHome ? 'text-primary' : 'text-foreground')}
            />
            <p className={cn(
              'text-sm font-medium truncate min-w-0',
              isMyHome ? 'text-primary' : 'text-foreground'
            )}>
              {homeTeam.name}
            </p>
            <p className="text-[10px] text-muted-foreground">
              {homeTeam.wins}-{homeTeam.losses}{homeTeam.ties ? `-${homeTeam.ties}` : ''}
            </p>
            <p className={cn(
              'text-2xl font-bold mt-1 tabular-nums',
              isFinal && homeWins ? 'text-damage' : isFinal && awayWins ? 'text-muted-foreground' : 'text-foreground'
            )}>
              {formatScore(homeScore)}
            </p>
            {/* SAB-994: projected total, starters only, hidden once final. */}
            {/* SAB-1009: PROJECTED FINAL under the actual -- banked + still expected, not a
                bare sum of projections. Hidden once nothing is left to play, so a finished
                matchup does not print its own score twice. */}
            {sides.home.projectedRemaining !== null && (
              <p className="text-[10px] text-muted-foreground tabular-nums">
                proj {sides.home.projectedFinal.toFixed(1)}
              </p>
            )}
            {/* SAB-1009: what is left, at a glance. */}
            {sides.home.yetToPlay.count > 0 && (
              <p className="text-[9px] text-muted-foreground/70 leading-tight mt-0.5">
                Yet to play ({sides.home.yetToPlay.count})
                <span className="block">{sides.home.yetToPlay.label}</span>
              </p>
            )}
            {isFinal && homeWins && (
              <div className="flex items-center justify-center gap-1 mt-0.5">
                <Trophy className="h-3 w-3 text-amber-400" />
                <span className="text-[10px] text-amber-400 font-bold">W</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* SAB-540 C3b: state-aware Drop CTA + active sabotages (store/deploy; no wheel).
          Only on the user's own matchup; targeted Drop only when unlocked. */}
      {isMyMatchup && (
        <MatchupSabotageBar
          leagueId={leagueId}
          matchupId={matchupId}
          week={week}
          status={status}
          myTeamId={isMyHome ? homeTeam.id : awayTeam.id}
          opponentName={isMyHome ? awayTeam.name : homeTeam.name}
          onNavigate={onNavigate}
        />
      )}

      {/* Roster Comparison */}
      {!compact && (
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="h-6 w-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            // SAB-1015: the roster comparison used to FORCE 440px and scroll sideways inside
            // the card, which at 390 is the "bleeds past both edges" in the report -- the
            // page never overflowed (documentElement scrollWidth == clientWidth at 390 and
            // 360), the card scrolled internally and clipped names at its edge.
            //
            // The floor was load-bearing while the two-sided row could not fit: the rows
            // measured 10px and 5px over their containers. They fit now (over: 0), so the
            // floor is dropped at mobile and the content sizes to the viewport, truncating
            // long names rather than pushing a scroll region. overflow-x-auto stays as a
            // safety net for anything unexpectedly wide; it should simply never engage.
            <div className="overflow-x-auto">
              <div className="min-w-0 sm:min-w-[440px]">
              {/* Starters Header */}
              <div className="flex items-center justify-between px-4 py-2 border-t border-border">
                <span className="text-[10px] text-muted-foreground uppercase tracking-wider font-medium">Starters</span>
                <span className="text-[10px] text-muted-foreground uppercase tracking-wider font-medium">Starters</span>
              </div>
              <PastWeekNotes away={awayPlayers.past} home={homePlayers.past} awayName={awayTeam.name} homeName={homeTeam.name} />

              {/* Slot-by-slot comparison */}
              {starterSlotOrder.map((slot) => {
                const label = slotLabel(slot);
                const awayP = awayPlayers.starters.find(p => p.position_slot === slot);
                const homeP = homePlayers.starters.find(p => p.position_slot === slot);
                const awayPts = playerPts(awayP);
                const homePts = playerPts(homeP);
                const awayWinsSlot = awayPts > homePts && (awayPts !== 0 || homePts !== 0);
                const homeWinsSlot = homePts > awayPts && (awayPts !== 0 || homePts !== 0);
                // Conventional: positive = good (neutral foreground). Sabotage:
                // positive trash = red "damage", negative anti = green (unchanged).
                const ptsColor = (v: number) => isConventional
                  ? (v !== 0 ? 'text-foreground' : 'text-muted-foreground')
                  : (v > 0 ? 'text-damage' : v < 0 ? 'text-emerald-400' : 'text-muted-foreground');

                const bdFor = (p: SlotPlayer | undefined): BreakdownResult | null => {
                  if (!p || !terms) return null;
                  const c = ctxByPlayer[p.player_id];
                  const r = computeBreakdown({
                    stats: (c?.stats as Record<string, unknown>) ?? null,
                    team_lost: c?.team_lost ?? null,
                    position: c?.position ?? null,
                    outcome_final: c?.outcome_final ?? null, // SAB-1127
                    storedTrash: p.trash_points,
                    storedAnti: p.anti_points,
                  }, terms);
                  if (r.blocked === 'mismatch') {
                    // LOUD IN LOGS, SILENT TO THE USER. A mismatch is a scoring discrepancy,
                    // not missing data; rendering it as "unavailable" would hide a real bug.
                    console.error(
                      '[nfl-breakdown] SCORING MISMATCH: per-term sum does not equal the stored score',
                      { player_id: p.player_id, name: p.full_name, slot,
                        storedTrash: p.trash_points, storedAnti: p.anti_points },
                    );
                  }
                  return r;
                };
                const awayBd = bdFor(awayP);
                const homeBd = bdFor(homeP);
                // Offer the chevron only when expanding would show something. A row where both
                // sides mismatch stays closed rather than opening onto an empty panel.
                const showable = (r: BreakdownResult | null) =>
                  !!r && (r.blocked === null || r.blocked === 'no-stats' || r.blocked === 'missing-context');
                const canExpand = showable(awayBd) || showable(homeBd);
                const isExpanded = expandedSlot === slot;

                return (
                  <div key={slot}>
                  <div
                    className="flex items-center gap-0 border-t border-border/50 text-xs"
                  >
                    {/* Away player */}
                    <div className={cn(
                      'flex-1 flex items-center gap-1 sm:gap-2 py-2 pl-2 sm:pl-4 pr-1 sm:pr-2 min-w-0',
                      awayWinsSlot && 'bg-damage/5'
                    )}>
                      {/* SAB-1046 follow-up: headshots are sm-AND-UP ONLY, and that is a
                          MEASURED call, not a preference.
                          
                          The matchup row is the tightest width budget in the app -- two
                          player identities, mirrored, sharing one line. Driving this fixture
                          with the longest names the player table has:
                          
                                          min name width   median   clipped
                            360 text-only       40px         69px      50
                            360 + avatars        4px         41px      70
                            390 text-only       51px         78px      44
                            390 + avatars       19px         56px      66
                          
                          A 4px name is not a truncated name, it is an absent one. The photo
                          would be legible and the player would not, which inverts what the
                          row is for. Above sm there is room and they render. */}
                      {awayP && (
                        <NFLPlayerAvatar
                          fullName={awayP.full_name}
                          position={awayP.position}
                          headshotUrl={awayP.headshot_url}
                          className="hidden sm:flex h-7 w-7"
                          textClassName="text-[9px]"
                        />
                      )}
                      <div className="min-w-0 sm:min-w-[120px] flex-1 text-left">
                        {awayP ? (
                          // SAB-1003: was `{team || position}` -- team OR position, never both, and no
                          // injury pill, bye or kickoff at all. Shared with My Team now.
                          <NFLPlayerMeta
                            align="left"
                            fullName={awayP.full_name}
                            playerId={awayP.player_id}
                            onOpenNews={() => { setDetailTab('news'); setDetailPlayerId(awayP.player_id); }}
                            shortName={shortPlayerName(awayP.full_name)}
                            position={awayP.position}
                            teamAlias={awayP.nfl_team_abbr}
                            byeWeek={awayP.byeWeek}
                            injuryStatus={awayP.injury_status}
                            status={awayP.status}
                            isBye={awayP.isBye}
                            game={awayP.game ? {
                              scheduled: awayP.game.scheduled, status: awayP.game.status,
                              opponentAlias: awayP.opponentAlias, isHome: awayP.game.isHome,
                            } : null}
                            onClick={() => setDetailPlayerId(awayP.player_id)}
                          />
                        ) : (
                          <p className="text-muted-foreground italic text-[11px]">{awayPlayers.past?.notRecorded ? 'Not recorded' : 'Empty'}</p>
                        )}
                      </div>
                      {/* SAB-1009 revision: the slot is a dash until the game is FINAL, then the
                          actual. The projection has moved to the second line, below. */}
                      <ScoreCell
                        projection={awayP && awayP.cents === undefined ? projectionLabel(weeklyProjMap.get(awayP.player_id)) : null}
                        cell={cellFor(awayP)}
                      />
                    </div>

                    {/* Slot label (center) — doubles as the breakdown toggle */}
                    <div className="w-7 sm:w-9 text-center shrink-0">
                      {canExpand ? (
                        <button
                          type="button"
                          onClick={() => setExpandedSlot(isExpanded ? null : slot)}
                          aria-label={`${isExpanded ? 'Hide' : 'Show'} ${label} scoring breakdown`}
                          aria-expanded={isExpanded}
                          className="w-full flex flex-col items-center text-muted-foreground hover:text-foreground transition-colors"
                        >
                          <span className={cn('text-[10px] font-bold', slotColors[label] || 'text-muted-foreground')}>
                            {label}
                          </span>
                          {isExpanded ? <ChevronUp className="h-2.5 w-2.5" /> : <ChevronDown className="h-2.5 w-2.5" />}
                        </button>
                      ) : (
                        <span className={cn('text-[10px] font-bold', slotColors[label] || 'text-muted-foreground')}>
                          {label}
                        </span>
                      )}
                    </div>

                    {/* Home player */}
                    <div className={cn(
                      'flex-1 flex items-center gap-1 sm:gap-2 py-2 pr-2 sm:pr-4 pl-1 sm:pl-2 min-w-0',
                      homeWinsSlot && 'bg-damage/5'
                    )}>
                      {/* SAB-1009 revision: the slot is a dash until the game is FINAL, then the
                          actual. The projection has moved to the second line, below. */}
                      <ScoreCell
                        projection={homeP && homeP.cents === undefined ? projectionLabel(weeklyProjMap.get(homeP.player_id)) : null}
                        cell={cellFor(homeP)}
                      />
                      <div className="min-w-0 sm:min-w-[120px] flex-1 text-right">
                        {homeP ? (
                          // SAB-1003: was `{team || position}` -- team OR position, never both, and no
                          // injury pill, bye or kickoff at all. Shared with My Team now.
                          <NFLPlayerMeta
                            align="right"
                            fullName={homeP.full_name}
                            playerId={homeP.player_id}
                            onOpenNews={() => { setDetailTab('news'); setDetailPlayerId(homeP.player_id); }}
                            shortName={shortPlayerName(homeP.full_name)}
                            position={homeP.position}
                            teamAlias={homeP.nfl_team_abbr}
                            byeWeek={homeP.byeWeek}
                            injuryStatus={homeP.injury_status}
                            status={homeP.status}
                            isBye={homeP.isBye}
                            game={homeP.game ? {
                              scheduled: homeP.game.scheduled, status: homeP.game.status,
                              opponentAlias: homeP.opponentAlias, isHome: homeP.game.isHome,
                            } : null}
                            onClick={() => setDetailPlayerId(homeP.player_id)}
                          />
                        ) : (
                          <p className="text-muted-foreground italic text-[11px]">{homePlayers.past?.notRecorded ? 'Not recorded' : 'Empty'}</p>
                        )}
                      </div>
                      {homeP && (
                        <NFLPlayerAvatar
                          fullName={homeP.full_name}
                          position={homeP.position}
                          headshotUrl={homeP.headshot_url}
                          className="hidden sm:flex h-7 w-7"
                          textClassName="text-[9px]"
                        />
                      )}
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="flex items-start gap-2 px-4 py-2 bg-muted/20 border-t border-border/50">
                      <BreakdownSide player={awayP} result={awayBd} align="left" />
                      <div className="w-7 sm:w-9 shrink-0" />
                      <BreakdownSide player={homeP} result={homeBd} align="right" />
                    </div>
                  )}
                  </div>
                );
              })}

              <AlsoCountedRows
                away={awayPlayers.past?.alsoCounted ?? []}
                home={homePlayers.past?.alsoCounted ?? []}
                onOpen={(id) => setDetailPlayerId(id)}
              />

              {/* Bench toggle */}
              {(awayPlayers.bench.length > 0 || homePlayers.bench.length > 0) && (
                <>
                  <button
                    onClick={() => setShowBench(!showBench)}
                    className="w-full flex items-center justify-center gap-1.5 py-2 border-t border-border text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <span className="text-[10px] uppercase tracking-wider font-medium">
                      Bench ({Math.max(awayPlayers.bench.length, homePlayers.bench.length)})
                    </span>
                    {showBench ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                  </button>

                  {showBench && (
                    <div>
                      {Array.from({ length: Math.max(awayPlayers.bench.length, homePlayers.bench.length) }).map((_, i) => {
                        const awayP = awayPlayers.bench[i];
                        const homeP = homePlayers.bench[i];
                        return (
                          <div key={i} className="flex items-center gap-0 border-t border-border/50 text-xs">
                            <div className="flex-1 flex items-center gap-1 sm:gap-2 py-1.5 pl-2 sm:pl-4 pr-1 sm:pr-2 min-w-0">
                              {awayP ? (
                                <>
                                  <div className="min-w-0 sm:min-w-[120px] flex-1">
                                    {/* SAB-1003: the bench cell showed a bare name -- no team, position,
                                        bye, kickoff or designation, strictly less than the starter row
                                        above it for no stated reason. Same block now. */}
                                    <NFLPlayerMeta
                                      align="left"
                                      fullName={awayP.full_name}
                            playerId={awayP.player_id}
                            onOpenNews={() => { setDetailTab('news'); setDetailPlayerId(awayP.player_id); }}
                                      shortName={shortPlayerName(awayP.full_name)}
                                      position={awayP.position}
                                      teamAlias={awayP.nfl_team_abbr}
                                      byeWeek={awayP.byeWeek}
                                      injuryStatus={awayP.injury_status}
                                      status={awayP.status}
                                      isBye={awayP.isBye}
                                      game={awayP.game ? {
                                        scheduled: awayP.game.scheduled, status: awayP.game.status,
                                        opponentAlias: awayP.opponentAlias, isHome: awayP.game.isHome,
                                      } : null}
                                      onClick={() => setDetailPlayerId(awayP.player_id)}
                                    />
                                  </div>
                                  {/* SAB-1009 revision: same shape as the starter rows -- dash until
                                      final, projection on the line below. */}
                                  <ScoreCell
                                    projection={awayP.cents === undefined ? projectionLabel(weeklyProjMap.get(awayP.player_id)) : null}
                                    cell={cellFor(awayP)}
                                  />
                                </>
                              ) : <div className="flex-1" />}
                            </div>

                            <div className="w-7 sm:w-9 text-center shrink-0">
                              <span className="text-[10px] font-bold text-muted-foreground">
                                {(awayP ?? homeP)?.cents !== undefined ? ((awayP ?? homeP)!.position_slot.startsWith('IR') ? 'IR' : 'BN') : 'BN'}
                              </span>
                            </div>

                            <div className="flex-1 flex items-center gap-1 sm:gap-2 py-1.5 pr-2 sm:pr-4 pl-1 sm:pl-2 min-w-0">
                              {homeP ? (
                                <>
                                  {/* SAB-1009 revision: same shape as the starter rows -- dash until
                                      final, projection on the line below. */}
                                  <ScoreCell
                                    projection={homeP.cents === undefined ? projectionLabel(weeklyProjMap.get(homeP.player_id)) : null}
                                    cell={cellFor(homeP)}
                                  />
                                  <div className="min-w-0 sm:min-w-[120px] flex-1">
                                    {/* SAB-1003: the bench cell showed a bare name -- no team, position,
                                        bye, kickoff or designation, strictly less than the starter row
                                        above it for no stated reason. Same block now. */}
                                    <NFLPlayerMeta
                                      align="right"
                                      fullName={homeP.full_name}
                            playerId={homeP.player_id}
                            onOpenNews={() => { setDetailTab('news'); setDetailPlayerId(homeP.player_id); }}
                                      shortName={shortPlayerName(homeP.full_name)}
                                      position={homeP.position}
                                      teamAlias={homeP.nfl_team_abbr}
                                      byeWeek={homeP.byeWeek}
                                      injuryStatus={homeP.injury_status}
                                      status={homeP.status}
                                      isBye={homeP.isBye}
                                      game={homeP.game ? {
                                        scheduled: homeP.game.scheduled, status: homeP.game.status,
                                        opponentAlias: homeP.opponentAlias, isHome: homeP.game.isHome,
                                      } : null}
                                      onClick={() => setDetailPlayerId(homeP.player_id)}
                                    />
                                  </div>
                                </>
                              ) : <div className="flex-1" />}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </>
              )}
              </div>
            </div>
          )}
        </CardContent>
      )}
      {/* Player Detail Modal */}
      <NFLPlayerDetailModal
        onTrade={onTradeWith ? (teamId, pid) => onTradeWith(teamId, pid) : undefined}
        playerId={detailPlayerId}
        initialTab={detailTab}
        open={!!detailPlayerId}
        onOpenChange={(open) => { if (!open) { setDetailPlayerId(null); setDetailTab(undefined); } }}
      />
    </Card>
    </ActiveNewsProvider>
  );
}

/* ------------------------------------------------------------------ */
/*  SAB-540 C3b: matchup Drop CTA + active sabotages                   */
/* ------------------------------------------------------------------ */

function MatchupSabotageBar({
  leagueId, matchupId, week, status, myTeamId, opponentName, onNavigate,
}: {
  leagueId: string | null;
  matchupId: string;
  week: number;
  status: string;
  myTeamId: string;
  opponentName: string;
  onNavigate?: (tab: string) => void;
}) {
  const [inv, setInv] = useState<any[]>([]);
  const [events, setEvents] = useState<any[]>([]);
  const [cards, setCards] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState(true);
  const [deployCtx, setDeployCtx] = useState<DeployContext | null>(null);

  // Locked = games have started or the matchup is done; no targeted Drop then.
  const locked = status === 'live' || status === 'in_progress' || status === 'final' || status === 'completed';

  const refresh = useCallback(async () => {
    if (!leagueId) { setLoading(false); return; }
    const [evRes, cardRes, invRes] = await Promise.all([
      supabase.from('nfl_sabotage_events').select('id, card_id, attacker_team_id, target_team_id, week, status').eq('league_id', leagueId).eq('week', week).eq('status', 'active'),
      supabase.from('nfl_sabotage_cards').select('id, name, emoji, rarity, target_scope'),
      locked ? Promise.resolve({ data: [] } as any)
        : supabase.from('nfl_sabotage_inventory').select('id, card_id, matchup_id, expires_at, is_used').eq('team_id', myTeamId).eq('is_used', false),
    ]);
    setEvents((evRes as any).data || []);
    setCards(Object.fromEntries((((cardRes as any).data) || []).map((c: any) => [c.id, c])));
    setInv((invRes as any).data || []);
    setLoading(false);
  }, [leagueId, myTeamId, week, locked]);

  useEffect(() => { refresh(); }, [refresh]);

  if (loading) return null;

  const cardOf = (id: string) => cards[id];
  const playedThisWeek = events.some((e) => e.attacker_team_id === myTeamId);
  const leagueSlotTaken = events.some((e) => cardOf(e.card_id)?.target_scope === 'league');

  // A card that would actually deploy right now (honest CTA — never offer a failing Drop).
  const deployable = inv.find((i) => {
    const c = cardOf(i.card_id);
    if (!c) return false;
    if (c.target_scope === 'league') return !leagueSlotTaken;
    return !i.matchup_id || i.matchup_id === matchupId;
  });
  const hasRollingLegendary = inv.some((i) => cardOf(i.card_id)?.target_scope === 'league') && leagueSlotTaken;

  const openDeploy = (i: any) => {
    const c = cardOf(i.card_id);
    if (!c || !leagueId) return;
    setDeployCtx({
      leagueId, matchupId: i.matchup_id || matchupId, opponentName, week, inventoryId: i.id,
      card: { name: c.name, emoji: c.emoji, target_scope: c.target_scope, rarity: c.rarity },
    });
  };

  // Active sabotages touching this matchup (both directions + any league-wide).
  const activeOnMatchup = events.filter((e) => {
    const c = cardOf(e.card_id);
    return c?.target_scope === 'league' || e.attacker_team_id === myTeamId || e.target_team_id === myTeamId || e.matchup_id === matchupId;
  });

  return (
    <>
      <div className="px-4 py-3 border-t border-border">
        {locked ? (
          <div className="text-center text-[11px] text-muted-foreground py-1">Matchup locked. Sabotages are closed.</div>
        ) : playedThisWeek ? (
          <div className="flex items-center justify-center gap-1.5 text-xs text-muted-foreground py-1.5">
            <Bomb className="h-4 w-4" /> Sabotage used this week
          </div>
        ) : deployable ? (
          <button onClick={() => openDeploy(deployable)} className="w-full h-11 rounded-xl text-white font-display font-bold flex items-center justify-center gap-2" style={{ background: SABOTAGE_GRADIENT }}>
            <Bomb className="h-5 w-5" /> Drop Sabotage on {opponentName}
          </button>
        ) : (
          <div className="space-y-1.5">
            <button onClick={() => onNavigate?.('sabotages')} className="w-full h-11 rounded-xl border border-primary/50 text-primary font-display font-bold flex items-center justify-center gap-2 hover:bg-primary/10">
              <ShoppingCart className="h-4 w-4" /> Get a Sabotage
            </button>
            {hasRollingLegendary && (
              <p className="text-[10px] text-center text-muted-foreground">Your league-wide Legendary rolls over to next week (this week's slot is taken).</p>
            )}
          </div>
        )}
      </div>

      {activeOnMatchup.length > 0 && (
        <div className="px-4 pb-3 space-y-1.5">
          <p className="text-[10px] font-display font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
            <Target className="h-3 w-3 text-damage" /> Sabotages in play · your league sees these
          </p>
          {activeOnMatchup.map((e) => {
            const c = cardOf(e.card_id);
            const rk = rarityOf(c?.rarity || 'common');
            const lw = c?.target_scope === 'league';
            const mine = e.attacker_team_id === myTeamId;
            const dir = lw ? 'League-wide' : mine ? `You dropped on ${opponentName}` : `${opponentName} dropped on you`;
            return (
              <div key={e.id} className="flex items-center gap-2.5 rounded-lg border border-damage/30 bg-damage/[0.06] p-2">
                <ArtChip emoji={c?.emoji ?? null} r={rk} size={32} />
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-semibold text-foreground truncate">{c?.name || 'Unknown'}</p>
                  <p className="text-[10px] text-muted-foreground">{dir}</p>
                </div>
                <Badge className="text-[8px] bg-damage/15 text-damage border-damage/30">LIVE · PUBLIC</Badge>
              </div>
            );
          })}
        </div>
      )}

      <SabotageDeployDialog ctx={deployCtx} onClose={() => setDeployCtx(null)} onDeployed={refresh} />
    </>
  );
}
